import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { api } from "../lib/api";
import { Icon } from "./Common.jsx";
import { useSession } from "../lib/auth-client";
import { useApp } from "../store.jsx";
import SharedSupportChat from "./chat/SupportChat.jsx";
import {
  listMyTickets,
  getMyTicket,
  listMyTicketMessages,
  postMyTicketMessage,
  reopenMyTicket,
} from "../lib/supportApi";
import useSupportUnread from "../hooks/useSupportUnread";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3000";
const STORAGE_KEY = "botWidget.state";
// Bumped to .v2 to discard any stale saved drag position that left the bubble
// floating over page content — it now starts back in the bottom-right corner.
const POS_KEY = "botWidget.pos.v2";

const persistState = (state) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
};
const loadPersistedState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
};

const STATUS_LABEL = { open: "Open", in_progress: "In progress", resolved: "Resolved", closed: "Closed" };
const STATUS_BADGE_BG = {
  open: "rgba(239,68,68,0.15)",
  in_progress: "rgba(59,130,246,0.15)",
  resolved: "rgba(34,197,94,0.15)",
  closed: "rgba(148,163,184,0.15)",
};
const STATUS_BADGE_FG = {
  open: "#fca5a5",
  in_progress: "#93c5fd",
  resolved: "#86efac",
  closed: "#cbd5e1",
};

function BotAvatar({ size = 28 }) {
  return (
    <div
      style={{
        width: size, height: size, borderRadius: 8, flexShrink: 0,
        background: "linear-gradient(135deg, #8B5CF6, #7C3AED)",
        display: "grid", placeItems: "center",
      }}
    >
      <Icon name="bar-chart-3" size={size * 0.5} style={{ color: "#fff" }} />
    </div>
  );
}

function TypingDots() {
  return (
    <div style={{ display: "flex", gap: 4, padding: "4px 0" }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 6, height: 6, borderRadius: "50%",
            background: "var(--primary-2, #A78BFA)", opacity: 0.6,
            animation: `chatDotPulse 1.2s ease-in-out ${i * 0.2}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

function Message({ role, content }) {
  const isUser = role === "user";
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: isUser ? "flex-end" : "flex-start", marginBottom: 10 }}>
      {!isUser && <BotAvatar size={26} />}
      <div
        style={{
          maxWidth: "78%",
          padding: "10px 14px",
          borderRadius: isUser ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
          background: isUser
            ? "linear-gradient(135deg, #8B5CF6, #7C3AED)"
            : "var(--surface-2, #1e293b)",
          color: isUser ? "#fff" : "var(--text, #e5e7eb)",
          fontSize: 13.5, lineHeight: 1.55,
          whiteSpace: "pre-wrap", wordBreak: "break-word",
          boxShadow: isUser ? "0 2px 8px rgba(139,92,246,0.2)" : "0 1px 4px rgba(0,0,0,0.08)",
        }}
      >
        {content}
      </div>
    </div>
  );
}

function MenuCard({ icon, iconColor, label, desc, onClick, rightBadge }) {
  return (
    <button
      aria-label={label}
      onClick={onClick}
      style={{
        width: "100%", padding: "14px 16px", borderRadius: 12,
        border: "1px solid var(--border, #1f2937)",
        background: "var(--surface, #0f172a)",
        cursor: "pointer", textAlign: "left",
        display: "flex", alignItems: "center", gap: 12,
        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--primary, #8B5CF6)";
        e.currentTarget.style.boxShadow = "0 0 0 1px var(--primary-glow, rgba(139,92,246,0.25)), 0 4px 12px rgba(0,0,0,0.15)";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--border, #1f2937)";
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "none";
      }}
    >
      <div
        style={{
          width: 38, height: 38, borderRadius: 10, flexShrink: 0,
          background: iconColor || "rgba(139,92,246,0.12)",
          display: "grid", placeItems: "center",
        }}
      >
        <Icon name={icon} size={18} style={{ color: "var(--primary-2, #A78BFA)" }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text, #f1f5f9)" }}>{label}</div>
        <div style={{ fontSize: 12, color: "var(--text-muted, #94a3b8)", marginTop: 1 }}>{desc}</div>
      </div>
      {rightBadge}
      <Icon name="chevron-right" size={14} style={{ color: "var(--text-muted, #64748b)" }} />
    </button>
  );
}

function TicketRow({ ticket, onClick, unread, pulse }) {
  return (
    <button
      aria-label={`Ticket #${ticket.id.slice(0, 8)} — ${STATUS_LABEL[ticket.status] || ticket.status}${unread > 0 ? `, ${unread} unread` : ""}`}
      onClick={onClick}
      style={{
        width: "100%", textAlign: "left", padding: "10px 12px",
        borderRadius: 10, border: "1px solid var(--border, #1f2937)",
        background: "var(--surface, #0f172a)", cursor: "pointer",
        display: "flex", alignItems: "center", gap: 10,
        animation: pulse && unread > 0 ? "ticketUnreadPulse 2s ease-in-out infinite" : "none",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--primary, #8B5CF6)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border, #1f2937)"; }}
    >
      <span
        style={{
          padding: "2px 8px", borderRadius: 6,
          fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
          background: STATUS_BADGE_BG[ticket.status] || "rgba(148,163,184,0.15)",
          color: STATUS_BADGE_FG[ticket.status] || "#cbd5e1",
          whiteSpace: "nowrap",
        }}
      >
        {STATUS_LABEL[ticket.status] || ticket.status}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: "var(--text, #f1f5f9)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          #{ticket.id.slice(0, 8)} · {ticket.message?.slice(0, 40)}
        </div>
        <div style={{ fontSize: 10, color: "var(--text-muted, #64748b)" }}>
          {ticket.messageCount} message{ticket.messageCount === 1 ? "" : "s"}
        </div>
      </div>
      {unread > 0 && (
        <span
          style={{
            padding: "2px 8px", borderRadius: 999,
            background: "#ef4444", color: "#fff",
            fontSize: 11, fontWeight: 700,
          }}
        >
          {unread}
        </span>
      )}
    </button>
  );
}

export default function ChatBot({ mode: appMode = "app" }) {
  const persisted = loadPersistedState();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(persisted?.view || "menu");
  const [botMode, setBotMode] = useState(null);
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [supportForm, setSupportForm] = useState(persisted?.supportForm || { name: "", phone: "", email: "", message: "" });
  const [supportSending, setSupportSending] = useState(false);
  const [supportSent, setSupportSent] = useState(false);
  const [tickets, setTickets] = useState({ active: [], recentClosed: [] });
  const [activeTicketId, setActiveTicketId] = useState(persisted?.ticketId || null);
  const [activeTicket, setActiveTicket] = useState(null);
  const [ticketLoading, setTicketLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const isPublic = appMode === "public";

  // ── Draggable widget position (desktop only). {x,y} = FAB top-left in px ──
  const [pos, setPos] = useState(() => {
    try { const raw = localStorage.getItem(POS_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
  });
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 480px)").matches,
  );
  const [, setResizeTick] = useState(0);
  const dragRef = useRef(null);
  const draggedRef = useRef(false);

  const { count: unreadCount, refresh: refreshUnread } = useSupportUnread();
  const { data: session } = useSession();
  const { toast } = useApp();
  const sessionUser = session?.user;

  // Auto-fill the support form with the signed-in user's contact details so
  // they don't have to type name/email/phone every time. Only writes empty
  // fields, preserving any draft the user already had in flight.
  useEffect(() => {
    if (isPublic || !sessionUser) return;
    setSupportForm((prev) => {
      const next = {
        name: prev.name || sessionUser.name || "",
        email: prev.email || sessionUser.email || "",
        phone: prev.phone || sessionUser.phone || "",
        message: prev.message || "",
      };
      if (
        next.name === prev.name &&
        next.email === prev.email &&
        next.phone === prev.phone &&
        next.message === prev.message
      ) return prev;
      return next;
    });
  }, [isPublic, sessionUser]);

  // Persist key UX state across widget close/open + page reload.
  useEffect(() => {
    persistState({ view, ticketId: activeTicketId, supportForm });
  }, [view, activeTicketId, supportForm]);

  // Honor email deep-link: ?openTicket=<id> opens the bot directly on that thread.
  useEffect(() => {
    if (isPublic) return;
    const params = new URLSearchParams(window.location.search);
    const openTicket = params.get("openTicket");
    if (openTicket) {
      setOpen(true);
      setActiveTicketId(openTicket);
      setView("ticket");
      params.delete("openTicket");
      const search = params.toString();
      const url = window.location.pathname + (search ? `?${search}` : "") + window.location.hash;
      window.history.replaceState({}, "", url);
    }
  }, [isPublic]);

  // Load user's tickets whenever the widget opens or after a send.
  const refreshTickets = useCallback(async () => {
    if (isPublic) return;
    try {
      const data = await listMyTickets();
      setTickets({ active: data.active || [], recentClosed: data.recentClosed || [] });
    } catch {
      // silent — keep stale list
    }
  }, [isPublic]);

  useEffect(() => {
    if (!open || isPublic) return;
    refreshTickets();
    const id = setInterval(refreshTickets, 30 * 1000);
    return () => clearInterval(id);
  }, [open, refreshTickets, isPublic]);

  // Whenever the user enters ticket-chat view, fetch the ticket's metadata
  // so we know its current status (needed to lock the chat when closed).
  useEffect(() => {
    if (view !== "ticket" || !activeTicketId || isPublic) return;
    let cancelled = false;
    setTicketLoading(true);
    getMyTicket(activeTicketId)
      .then((t) => { if (!cancelled) setActiveTicket(t); })
      .catch(() => { if (!cancelled) setActiveTicket(null); })
      .finally(() => { if (!cancelled) setTicketLoading(false); });
    const id = setInterval(() => {
      getMyTicket(activeTicketId).then((t) => { if (!cancelled) setActiveTicket(t); }).catch(() => {});
    }, 20 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [view, activeTicketId, isPublic]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history, loading]);

  useEffect(() => {
    if (open && view === "chat") setTimeout(() => inputRef.current?.focus(), 100);
  }, [open, view]);

  // ── Draggable widget (desktop) ──────────────────────────────────────────
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    if (pos) { try { localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch {} }
  }, [pos]);

  // Keep a saved position inside the viewport (and re-anchor the open panel)
  // whenever the window resizes.
  useEffect(() => {
    if (isMobile) return;
    const onResize = () => {
      setResizeTick((t) => t + 1);
      setPos((p) => {
        if (!p) return p;
        const size = 56;
        const x = Math.min(Math.max(8, p.x), window.innerWidth - size - 8);
        const y = Math.min(Math.max(8, p.y), window.innerHeight - size - 8);
        return x === p.x && y === p.y ? p : { x, y };
      });
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isMobile]);

  const FAB_SIZE = 56;
  const onFabPointerDown = (e) => {
    if (isMobile) return; // native bottom-right anchor on phones
    const startLeft = pos ? pos.x : window.innerWidth - FAB_SIZE - 24;
    const startTop = pos ? pos.y : window.innerHeight - FAB_SIZE - 24;
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: startLeft, origY: startTop, moved: false };
    draggedRef.current = false;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  };
  const onFabPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) > 4) d.moved = true; // ignore micro-jitter
    if (!d.moved) return;
    draggedRef.current = true;
    const x = Math.min(Math.max(8, d.origX + dx), window.innerWidth - FAB_SIZE - 8);
    const y = Math.min(Math.max(8, d.origY + dy), window.innerHeight - FAB_SIZE - 8);
    setPos({ x, y });
  };
  const onFabPointerUp = (e) => {
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
  };

  // Anchor the open panel next to the FAB, clamped to the viewport. On mobile
  // the panel is full-screen via CSS, so keep the original corner anchor.
  const getPanelPos = () => {
    if (isMobile || !pos) return { bottom: 92, right: 24 };
    const Wp = 380;
    const Hp = Math.min(600, window.innerHeight - 116);
    const margin = 12;
    const cx = pos.x + FAB_SIZE / 2;
    const cy = pos.y + FAB_SIZE / 2;
    let left = cx > window.innerWidth / 2 ? pos.x + FAB_SIZE - Wp : pos.x;
    left = Math.min(Math.max(margin, left), window.innerWidth - Wp - margin);
    let top = cy > window.innerHeight / 2 ? pos.y - Hp - margin : pos.y + FAB_SIZE + margin;
    top = Math.min(Math.max(margin, top), window.innerHeight - Hp - margin);
    return { left, top };
  };

  const selectMode = (m) => { setBotMode(m); setHistory([]); setView("chat"); };
  const goMenu = () => { setView("menu"); setBotMode(null); setHistory([]); setActiveTicket(null); };
  const openSupport = () => {
    setSupportSent(false);
    // Reset to a fresh form, but seed the contact fields from the signed-in
    // user so the customer only has to type the message itself.
    setSupportForm({
      name: sessionUser?.name || "",
      email: sessionUser?.email || "",
      phone: sessionUser?.phone || "",
      message: "",
    });
    setView("support");
  };
  const openTicket = (id) => { setActiveTicketId(id); setView("ticket"); };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setHistory((h) => [...h, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const url = isPublic ? `${API_BASE}/api/chat/public` : `${API_BASE}/api/chat`;
      const payload = isPublic ? { message: text, history } : { message: text, history, bot_mode: botMode };
      const { data } = await axios.post(url, payload, { withCredentials: true });
      let reply = data.reply || "";
      const needsHelp = reply.includes("[NEED_HELP]");
      reply = reply.replace(/\s*\[NEED_HELP\]\s*/g, "").trim();
      setHistory((h) => [...h, { role: "assistant", content: reply }]);
      if (needsHelp) {
        setTimeout(() => {
          setHistory((h) => [...h, { role: "assistant", content: "I wasn't able to fully answer that. Would you like to talk to our support team?", showSupport: true }]);
        }, 500);
      }
    } catch {
      setHistory((h) => [...h, { role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const submitSupport = async () => {
    if (!supportForm.name.trim() || !supportForm.message.trim()) return;
    setSupportSending(true);
    try {
      // api.post returns the parsed JSON body directly — { id, message } —
      // so no { data } destructure.
      const result = await api.post("/api/support/ticket", {
        ...supportForm,
        source: isPublic ? "landing_page" : "chatbot",
      });
      setSupportSent(true);
      if (!isPublic && result?.id) {
        await refreshTickets();
        setTimeout(() => openTicket(result.id), 600);
      }
    } catch { toast("Failed to submit. Please try again.", "error"); }
    finally { setSupportSending(false); }
  };

  const modeLabel = botMode === "data" ? "My Data" : botMode === "help" ? "Help Guide" : "AI Assistant";
  const ticketLocked = activeTicket && activeTicket.status === "closed";
  const ticketIsResolved = activeTicket && activeTicket.status === "resolved";

  const ticketsApi = {
    listMessages: (id) => listMyTicketMessages(id),
    postMessage: async (id, body) => {
      const r = await postMyTicketMessage(id, body);
      refreshUnread();
      refreshTickets();
      return r;
    },
  };

  return (
    <>
      <style>{`
        @keyframes chatDotPulse { 0%,80%,100%{opacity:.3;transform:scale(.8)} 40%{opacity:1;transform:scale(1)} }
        @keyframes chatSlideUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes chatFabPulse { 0%{box-shadow:0 4px 16px rgba(139,92,246,0.3)} 50%{box-shadow:0 4px 24px rgba(139,92,246,0.5)} 100%{box-shadow:0 4px 16px rgba(139,92,246,0.3)} }
        @keyframes ticketUnreadPulse { 0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.2)} 50%{box-shadow:0 0 0 4px rgba(239,68,68,0.08)} }

        /* ─────────────────────────────────────────────────────────────
           Responsive / mobile. The widget is styled with inline styles
           (the desktop source of truth); the rules below override only on
           small screens, short viewports and touch devices. env() resolves
           to 0 where there are no insets, so this is a no-op on desktop. */

        /* Phones: the panel becomes an edge-to-edge full screen instead of
           a fixed 380px card floating in the corner. height:100dvh tracks
           the *dynamic* viewport so it shrinks above the soft keyboard. */
        @media (max-width: 480px) {
          .chatbot-panel {
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            width: 100% !important;
            height: 100dvh !important;
            max-height: 100dvh !important;
            border-radius: 0 !important;
          }
          .chatbot-header {
            padding-left: 12px !important;
            padding-right: 12px !important;
            padding-top: calc(14px + env(safe-area-inset-top, 0px)) !important;
          }
          .chatbot-chat-scroll { padding-left: 10px !important; padding-right: 10px !important; }
          .chatbot-input-row { padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px)) !important; }
          .chatbot-support-form { padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px)) !important; }
          /* 16px inputs stop iOS Safari from auto-zooming on focus. */
          .chatbot-textarea, .chatbot-field { font-size: 16px !important; }
          /* Smaller, safe-area-inset FAB; hidden while the panel is open
             (the full-screen header carries its own close button). */
          .chatbot-fab {
            width: 52px !important;
            height: 52px !important;
            bottom: calc(16px + env(safe-area-inset-bottom, 0px)) !important;
            right: calc(16px + env(safe-area-inset-right, 0px)) !important;
          }
          .chatbot-fab--open { display: none !important; }
        }

        /* Older iOS (<15.4) / old Chrome without dvh fall back to vh — the
           inline min()/100dvh declarations are dropped there as invalid. */
        @supports not (height: 100dvh) {
          .chatbot-panel { max-height: 600px; }
          @media (max-width: 480px) {
            .chatbot-panel { height: 100vh !important; max-height: 100vh !important; }
          }
        }

        /* Short viewport (landscape / keyboard open): cap the composer. */
        @media (max-height: 600px) {
          .chatbot-textarea { max-height: 64px !important; }
        }

        /* Touch devices: WCAG-sized 44px tap targets. */
        @media (hover: none) and (pointer: coarse) {
          .chatbot-header-btn { width: 44px !important; height: 44px !important; }
          .chatbot-send-btn { width: 44px !important; height: 44px !important; }
        }

        /* Touch: press feedback via :active, because the JS hover styling
           never clears on touch (mouseenter fires once, mouseleave never). */
        @media (hover: none) {
          .chatbot-menu-card:active {
            border-color: var(--primary, #8B5CF6) !important;
            box-shadow: 0 0 0 1px var(--primary-glow, rgba(139,92,246,0.25)), 0 4px 12px rgba(0,0,0,0.15) !important;
            transform: translateY(-1px) !important;
          }
          .chatbot-ticket-row:active { border-color: var(--primary, #8B5CF6) !important; }
          .chatbot-fab:active {
            transform: scale(1.06) !important;
            box-shadow: 0 6px 28px rgba(139,92,246,0.45) !important;
          }
        }
      `}</style>

      <button
        onClick={() => { if (draggedRef.current) { draggedRef.current = false; return; } setOpen((o) => !o); }}
        onPointerDown={onFabPointerDown}
        onPointerMove={onFabPointerMove}
        onPointerUp={onFabPointerUp}
        aria-label={open ? "Close assistant" : "Open AI Assistant"}
        title={unreadCount > 0 ? `${unreadCount} new support message${unreadCount === 1 ? "" : "s"}` : "AI Assistant — drag to move"}
        className={`chatbot-fab${open ? " chatbot-fab--open" : ""}`}
        style={{
          position: "fixed",
          ...(!isMobile && pos
            ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" }
            : {
                bottom: "calc(24px + env(safe-area-inset-bottom, 0px))",
                right: "calc(24px + env(safe-area-inset-right, 0px))",
              }),
          zIndex: 1000,
          width: 56, height: 56, borderRadius: 16, border: "none",
          cursor: isMobile ? "pointer" : "grab",
          touchAction: isMobile ? "auto" : "none",
          background: "linear-gradient(135deg, #8B5CF6, #7C3AED)",
          color: "#fff", fontSize: 20,
          boxShadow: "0 4px 20px rgba(139,92,246,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          transform: open ? "rotate(0deg)" : "none",
          animation: open ? "none" : "chatFabPulse 3s ease-in-out infinite",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.boxShadow = "0 6px 28px rgba(139,92,246,0.45)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(139,92,246,0.35)"; }}
      >
        {open ? <Icon name="x" size={22} /> : <Icon name="message-circle" size={22} />}
        {!open && !isPublic && unreadCount > 0 && (
          <span
            style={{
              position: "absolute", top: -4, right: -4, minWidth: 22, height: 22,
              padding: "0 6px", borderRadius: 999,
              background: "#ef4444", color: "#fff",
              fontSize: 11, fontWeight: 700,
              display: "grid", placeItems: "center",
              boxShadow: "0 0 0 2px var(--bg, #0b1121)",
            }}
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="chatbot-panel"
          style={{
            position: "fixed", zIndex: 999,
            ...getPanelPos(),
            width: 380, maxHeight: "min(600px, calc(100dvh - 116px))",
            display: "flex", flexDirection: "column",
            borderRadius: 20,
            boxShadow: "0 12px 48px rgba(0,0,0,0.25), 0 0 0 1px rgba(139,92,246,0.1)",
            background: "var(--bg, #0b1121)",
            border: "1px solid var(--border, #1f2937)",
            overflow: "hidden",
            animation: "chatSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* Header */}
          <div
            className="chatbot-header"
            style={{
              padding: "14px 16px",
              background: "linear-gradient(135deg, rgba(139,92,246,0.12), rgba(124,58,237,0.06))",
              borderBottom: "1px solid var(--border, #1f2937)",
              display: "flex", alignItems: "center", gap: 10,
            }}
          >
            <BotAvatar size={32} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text, #f1f5f9)" }}>
                  {view === "support"
                    ? "Contact Support"
                    : view === "ticket"
                      ? `Support · #${activeTicketId?.slice(0, 8) || ""}`
                      : view === "tickets"
                        ? "Your Tickets"
                        : modeLabel}
                </span>
                {view === "chat" && (
                  <span
                    style={{
                      fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5,
                      padding: "2px 6px", borderRadius: 4,
                      background: botMode === "data" ? "rgba(59,130,246,0.15)" : "rgba(34,197,94,0.15)",
                      color: botMode === "data" ? "#60a5fa" : "#4ade80",
                    }}
                  >
                    {botMode === "data" ? "Live" : "KB"}
                  </span>
                )}
                {view === "ticket" && activeTicket && (
                  <span
                    style={{
                      fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5,
                      padding: "2px 6px", borderRadius: 4,
                      background: STATUS_BADGE_BG[activeTicket.status] || "rgba(148,163,184,0.15)",
                      color: STATUS_BADGE_FG[activeTicket.status] || "#cbd5e1",
                    }}
                  >
                    {STATUS_LABEL[activeTicket.status] || activeTicket.status}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted, #64748b)", marginTop: 1 }}>
                {view === "menu" ? "Choose how I can help"
                  : view === "support" ? "We'll reply right here"
                  : view === "ticket" ? "Replies appear live"
                  : view === "tickets" ? `${tickets.active.length} active`
                  : "Powered by ReportFlow AI"}
              </div>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {view !== "menu" && (
                <button
                  onClick={goMenu}
                  aria-label="Back to menu"
                  title="Back to menu"
                  className="chatbot-header-btn"
                  style={{
                    width: 30, height: 30, borderRadius: 8, border: "none",
                    background: "rgba(255,255,255,0.06)", cursor: "pointer",
                    display: "grid", placeItems: "center",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.06)"}
                >
                  <Icon name="arrow-left" size={14} style={{ color: "var(--text-muted, #94a3b8)" }} />
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                title="Close"
                className="chatbot-header-btn"
                style={{
                  width: 30, height: 30, borderRadius: 8, border: "none",
                  background: "rgba(255,255,255,0.06)", cursor: "pointer",
                  display: "grid", placeItems: "center",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.06)"}
              >
                <Icon name="minus" size={14} style={{ color: "var(--text-muted, #94a3b8)" }} />
              </button>
            </div>
          </div>

          {/* ── Menu ── */}
          {view === "menu" && (
            <div style={{ flex: 1, padding: "20px 16px", display: "flex", flexDirection: "column", gap: 10, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 4 }}>
                <div
                  style={{
                    width: 52, height: 52, borderRadius: 14,
                    background: "linear-gradient(135deg, #8B5CF6, #7C3AED)",
                    display: "grid", placeItems: "center",
                    boxShadow: "0 4px 16px rgba(139,92,246,0.3)",
                  }}
                >
                  <Icon name="bar-chart-3" size={24} style={{ color: "#fff" }} />
                </div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text, #f1f5f9)", marginTop: 14 }}>
                  Hi there! I'm ReportFlow AI
                </div>
                <div style={{ fontSize: 13, color: "var(--text-muted, #94a3b8)", marginTop: 4 }}>
                  What would you like help with?
                </div>
              </div>

              {!isPublic && (
                <MenuCard
                  icon="bar-chart-2"
                  iconColor="rgba(59,130,246,0.12)"
                  label="My Data"
                  desc="Tasks, reports & team analytics"
                  onClick={() => selectMode("data")}
                />
              )}
              <MenuCard
                icon="book-open"
                iconColor="rgba(34,197,94,0.12)"
                label={isPublic ? "How It Works" : "Help Guide"}
                desc="Platform features & how-to"
                onClick={() => selectMode("help")}
              />
              <MenuCard
                icon="headset"
                iconColor="rgba(249,115,22,0.12)"
                label={isPublic ? "Contact Us" : "Contact Support"}
                desc="Open a new support ticket"
                onClick={openSupport}
              />
              {!isPublic && tickets.active.length > 0 && (
                <MenuCard
                  icon="message-circle"
                  iconColor="rgba(239,68,68,0.12)"
                  label="Your tickets"
                  desc={`${tickets.active.length} active · ${unreadCount} unread`}
                  onClick={() => setView("tickets")}
                  rightBadge={unreadCount > 0 && (
                    <span
                      style={{
                        padding: "2px 8px", borderRadius: 999,
                        background: "#ef4444", color: "#fff",
                        fontSize: 11, fontWeight: 700,
                      }}
                    >
                      {unreadCount}
                    </span>
                  )}
                />
              )}

              <div style={{ marginTop: "auto", textAlign: "center", padding: "8px 0 0" }}>
                <span style={{ fontSize: 10, color: "var(--text-muted, #475569)", letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 600 }}>
                  <Icon name="bar-chart-3" size={10} style={{ verticalAlign: "middle", marginRight: 4 }} />
                  ReportFlow
                </span>
              </div>
            </div>
          )}

          {/* ── AI Chat ── */}
          {view === "chat" && (
            <>
              <div className="chatbot-chat-scroll" style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "14px 14px 6px", minHeight: 120 }}>
                {history.length === 0 && (
                  <div style={{ textAlign: "center", padding: "32px 12px" }}>
                    <div
                      style={{
                        width: 48, height: 48, borderRadius: 14, margin: "0 auto 12px",
                        background: "linear-gradient(135deg, rgba(139,92,246,0.15), rgba(124,58,237,0.08))",
                        display: "grid", placeItems: "center",
                      }}
                    >
                      <Icon
                        name={botMode === "data" ? "bar-chart-2" : "book-open"}
                        size={22}
                        style={{ color: "var(--primary-2, #A78BFA)" }}
                      />
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text, #f1f5f9)", marginBottom: 4 }}>
                      {botMode === "data" ? "Data Assistant" : "Help Guide"}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--text-muted, #94a3b8)", lineHeight: 1.5 }}>
                      {botMode === "data"
                        ? "Ask about your tasks, reports, or team analytics."
                        : "Ask how to use any feature on the platform."}
                    </div>
                  </div>
                )}
                {history.map((msg, i) => (
                  <div key={i}>
                    <Message role={msg.role} content={msg.content} />
                    {msg.showSupport && (
                      <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 10, paddingLeft: 34 }}>
                        <button
                          onClick={openSupport}
                          style={{
                            background: "linear-gradient(135deg, #8B5CF6, #7C3AED)",
                            color: "#fff", border: "none", borderRadius: 10,
                            padding: "8px 16px", cursor: "pointer",
                            fontSize: 13, fontWeight: 600,
                            display: "flex", alignItems: "center", gap: 6,
                            boxShadow: "0 2px 8px rgba(139,92,246,0.25)",
                            transition: "transform 0.15s, box-shadow 0.15s",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(139,92,246,0.35)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(139,92,246,0.25)"; }}
                        >
                          <Icon name="headset" size={14} /> Contact Support
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {loading && (
                  <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                    <BotAvatar size={26} />
                    <div
                      style={{
                        padding: "10px 14px", borderRadius: "14px 14px 14px 4px",
                        background: "var(--surface-2, #1e293b)",
                      }}
                    >
                      <TypingDots />
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              <div
                className="chatbot-input-row"
                style={{
                  padding: "10px 12px", borderTop: "1px solid var(--border, #1f2937)",
                  background: "rgba(0,0,0,0.15)",
                  display: "flex", gap: 8, alignItems: "flex-end",
                }}
              >
                <textarea
                  ref={inputRef}
                  className="chatbot-textarea"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="Type a message..."
                  rows={1}
                  style={{
                    flex: 1, resize: "none",
                    border: "1px solid var(--border, #1f2937)", borderRadius: 10,
                    padding: "10px 12px", fontSize: 13.5, outline: "none",
                    fontFamily: "inherit",
                    background: "var(--surface, #0f172a)",
                    color: "var(--text, #f1f5f9)",
                    maxHeight: 80, overflowY: "auto",
                    transition: "border-color 0.15s",
                  }}
                  onFocus={(e) => e.target.style.borderColor = "var(--primary, #8B5CF6)"}
                  onBlur={(e) => e.target.style.borderColor = "var(--border, #1f2937)"}
                />
                <button
                  aria-label="Send message"
                  onClick={sendMessage}
                  disabled={!input.trim() || loading}
                  className="chatbot-send-btn"
                  style={{
                    width: 38, height: 38, borderRadius: 10, border: "none",
                    background: input.trim() && !loading ? "linear-gradient(135deg, #8B5CF6, #7C3AED)" : "var(--surface-2, #1e293b)",
                    color: "#fff", cursor: input.trim() && !loading ? "pointer" : "not-allowed",
                    display: "grid", placeItems: "center", flexShrink: 0,
                    transition: "all 0.2s",
                    boxShadow: input.trim() && !loading ? "0 2px 8px rgba(139,92,246,0.3)" : "none",
                  }}
                >
                  <Icon name="send" size={16} />
                </button>
              </div>
            </>
          )}

          {/* ── Tickets list ── */}
          {view === "tickets" && (
            <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
              {tickets.active.length === 0 && tickets.recentClosed.length === 0 ? (
                <div style={{ textAlign: "center", padding: 32, color: "var(--text-muted, #94a3b8)", fontSize: 13 }}>
                  You haven't raised any tickets yet.
                </div>
              ) : (
                <>
                  {tickets.active.length > 0 && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted, #94a3b8)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Active
                      </div>
                      {tickets.active.map((t) => (
                        <TicketRow
                          key={t.id}
                          ticket={t}
                          unread={t.unreadCount}
                          pulse
                          onClick={() => openTicket(t.id)}
                        />
                      ))}
                    </>
                  )}
                  {tickets.recentClosed.length > 0 && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted, #94a3b8)", textTransform: "uppercase", letterSpacing: 0.5, marginTop: 8 }}>
                        Recently closed
                      </div>
                      {tickets.recentClosed.map((t) => (
                        <TicketRow
                          key={t.id}
                          ticket={t}
                          unread={0}
                          onClick={() => openTicket(t.id)}
                        />
                      ))}
                    </>
                  )}
                </>
              )}
              <button
                onClick={openSupport}
                style={{
                  marginTop: 8, padding: "10px 14px", borderRadius: 10,
                  background: "linear-gradient(135deg, #8B5CF6, #7C3AED)", color: "#fff",
                  border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                <Icon name="plus" size={14} /> Start a new ticket
              </button>
            </div>
          )}

          {/* ── Ticket chat ── */}
          {view === "ticket" && (
            <div style={{ flex: 1, padding: 12, display: "flex", flexDirection: "column", gap: 10, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
              {ticketLoading && !activeTicket ? (
                <div style={{ padding: 24, textAlign: "center", color: "var(--text-muted, #94a3b8)", fontSize: 13 }}>Loading…</div>
              ) : !activeTicket ? (
                <div style={{ padding: 24, textAlign: "center", color: "var(--text-muted, #94a3b8)", fontSize: 13 }}>
                  Could not load this ticket.
                </div>
              ) : (
                <>
                  {activeTicket.message && (
                    <div
                      style={{
                        padding: 10, borderRadius: 10,
                        background: "rgba(148,163,184,0.08)",
                        border: "1px solid var(--border, #1f2937)",
                        fontSize: 12, color: "var(--text-muted, #cbd5e1)",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted, #94a3b8)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                        Original request
                      </div>
                      {activeTicket.message}
                    </div>
                  )}
                  <SharedSupportChat
                    ticket={activeTicket}
                    role="user"
                    api={ticketsApi}
                    locked={ticketLocked}
                    compact
                    maxHeight="min(320px, calc(100dvh - 220px))"
                    headerLabel="Conversation with support"
                  />
                  {ticketLocked && (
                    <div
                      style={{
                        padding: 12, borderRadius: 10,
                        background: "rgba(148,163,184,0.12)",
                        border: "1px solid var(--border, #1f2937)",
                        textAlign: "center",
                      }}
                    >
                      <div style={{ fontSize: 13, color: "var(--text, #f1f5f9)", marginBottom: 8 }}>
                        This ticket is closed.
                      </div>
                      <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                        <button
                          onClick={goMenu}
                          style={{
                            padding: "8px 14px", borderRadius: 8, border: "1px solid var(--border, #1f2937)",
                            background: "var(--surface, #0f172a)", color: "var(--text, #f1f5f9)",
                            fontSize: 12, fontWeight: 600, cursor: "pointer",
                          }}
                        >
                          Back to assistant
                        </button>
                        <button
                          onClick={openSupport}
                          style={{
                            padding: "8px 14px", borderRadius: 8, border: "none",
                            background: "linear-gradient(135deg, #8B5CF6, #7C3AED)", color: "#fff",
                            fontSize: 12, fontWeight: 600, cursor: "pointer",
                          }}
                        >
                          Start a new ticket
                        </button>
                      </div>
                    </div>
                  )}
                  {ticketIsResolved && !ticketLocked && (
                    <button
                      onClick={async () => {
                        try {
                          const updated = await reopenMyTicket(activeTicket.id);
                          setActiveTicket(updated);
                        } catch {}
                      }}
                      style={{
                        padding: "8px 14px", borderRadius: 8, border: "1px solid var(--border, #1f2937)",
                        background: "var(--surface, #0f172a)", color: "var(--text, #f1f5f9)",
                        fontSize: 12, fontWeight: 600, cursor: "pointer",
                      }}
                    >
                      Reopen ticket
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Support form ── */}
          {view === "support" && (
            <div className="chatbot-support-form" style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "20px 16px" }}>
              {supportSent ? (
                <div style={{ textAlign: "center", padding: "36px 12px" }}>
                  <div
                    style={{
                      width: 56, height: 56, borderRadius: 16, margin: "0 auto 16px",
                      background: "rgba(34,197,94,0.12)", display: "grid", placeItems: "center",
                    }}
                  >
                    <Icon name="check-circle-2" size={28} style={{ color: "#4ade80" }} />
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text, #f1f5f9)", marginBottom: 6 }}>
                    Ticket created!
                  </div>
                  <div style={{ fontSize: 13, color: "var(--text-muted, #94a3b8)", lineHeight: 1.5 }}>
                    {isPublic
                      ? "Our support team will reach out to you soon."
                      : "Opening the chat — you can follow up here."}
                  </div>
                  {isPublic && (
                    <button
                      onClick={goMenu}
                      style={{
                        marginTop: 20, padding: "10px 24px", borderRadius: 10,
                        background: "linear-gradient(135deg, #8B5CF6, #7C3AED)", color: "#fff",
                        border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
                        boxShadow: "0 2px 8px rgba(139,92,246,0.25)",
                      }}
                    >
                      Back to menu
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div style={{
                    padding: "12px 14px", borderRadius: 10, marginBottom: 16,
                    background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.15)",
                    display: "flex", alignItems: "center", gap: 10,
                  }}>
                    <Icon name="headset" size={18} style={{ color: "var(--primary-2, #A78BFA)", flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: "var(--text, #e5e7eb)", lineHeight: 1.4 }}>
                      {isPublic
                        ? "Fill out the form and our team will reach out."
                        : "Create a ticket — admin replies will appear right here in the bot."}
                    </span>
                  </div>
                  {[
                    { key: "name", label: "Name", type: "text", icon: "user", required: true },
                    { key: "phone", label: "Phone", type: "tel", icon: "phone" },
                    { key: "email", label: "Email", type: "email", icon: "mail" },
                  ].map((f) => (
                    <div key={f.key} style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted, #94a3b8)", display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        {f.label} {f.required && <span style={{ color: "#ef4444" }}>*</span>}
                      </label>
                      <div style={{ position: "relative" }}>
                        <Icon
                          name={f.icon} size={14}
                          style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted, #64748b)" }}
                        />
                        <input
                          type={f.type}
                          value={supportForm[f.key]}
                          onChange={(e) => setSupportForm((s) => ({ ...s, [f.key]: e.target.value }))}
                          style={{ ...fieldStyle, paddingLeft: 32 }}
                          onFocus={(e) => e.target.style.borderColor = "var(--primary, #8B5CF6)"}
                          onBlur={(e) => e.target.style.borderColor = "var(--border, #1f2937)"}
                        />
                      </div>
                    </div>
                  ))}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted, #94a3b8)", display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Message <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <textarea
                      value={supportForm.message}
                      onChange={(e) => setSupportForm((s) => ({ ...s, message: e.target.value }))}
                      rows={3}
                      placeholder="Describe your issue..."
                      style={{ ...fieldStyle, resize: "vertical", minHeight: 70 }}
                      onFocus={(e) => e.target.style.borderColor = "var(--primary, #8B5CF6)"}
                      onBlur={(e) => e.target.style.borderColor = "var(--border, #1f2937)"}
                    />
                  </div>
                  <button
                    onClick={submitSupport}
                    disabled={!supportForm.name.trim() || !supportForm.message.trim() || supportSending}
                    style={{
                      width: "100%", padding: "11px 0", borderRadius: 10,
                      background: supportForm.name.trim() && supportForm.message.trim() && !supportSending
                        ? "linear-gradient(135deg, #8B5CF6, #7C3AED)"
                        : "var(--surface-2, #1e293b)",
                      color: "#fff", border: "none", fontSize: 14, fontWeight: 600,
                      cursor: supportForm.name.trim() && supportForm.message.trim() && !supportSending ? "pointer" : "not-allowed",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                      boxShadow: supportForm.name.trim() && supportForm.message.trim() && !supportSending
                        ? "0 2px 12px rgba(139,92,246,0.25)" : "none",
                      transition: "all 0.2s",
                    }}
                  >
                    <Icon name={supportSending ? "loader" : "send"} size={15} />
                    {supportSending ? "Submitting..." : "Create ticket"}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

const fieldStyle = {
  width: "100%", padding: "10px 12px", borderRadius: 10,
  border: "1px solid var(--border, #1f2937)",
  fontSize: 13, outline: "none", fontFamily: "inherit",
  background: "var(--surface, #0f172a)", color: "var(--text, #f1f5f9)",
  boxSizing: "border-box", transition: "border-color 0.15s",
};
